/**
 * Residual memory-paint aging — presentation only.
 * Flattens leftover sonar afterimages toward a planar (near-black) tone,
 * while solid terrain AABBs erase residual fill so walls stay readable cutouts.
 */

export type MemoryPaintBlock = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/** Minimal Graphics surface used for terrain erase masks. */
export type MemoryPaintEraseGfx = {
  clear(): void;
  fillStyle(color: number, alpha?: number): void;
  fillRect(x: number, y: number, width: number, height: number): void;
};

/**
 * Exponential approach factor for one frame of RT.fill.
 * `fadePerSec` 0.28 ≈ ~25% residual chroma left after 5s.
 */
export function residualFadeAlpha(dt: number, fadePerSec: number): number {
  const rate = Math.max(0, fadePerSec);
  if (rate <= 0 || dt <= 0) return 0;
  return 1 - Math.exp(-rate * dt);
}

/**
 * Draw solid AABB interiors (center-based, same as `inBlock`) as an erase mask.
 * Residual paint must not fill terrain — silhouettes stay cutouts when looking back.
 */
export function fillSolidBlockEraseMask(
  gfx: MemoryPaintEraseGfx,
  blocks: readonly MemoryPaintBlock[],
): void {
  gfx.clear();
  gfx.fillStyle(0xffffff, 1);
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!;
    const hw = b.w * 0.5;
    const hh = b.h * 0.5;
    gfx.fillRect(b.x - hw, b.y - hh, b.w, b.h);
  }
}
